import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ProductsTrackerTable } from '../components/ProductsTrackerTable';
import { SocialMediaProspectingTracker } from '../components/SocialMediaProspectingTracker';
import api from '../utils/api';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, DollarSign, Target, Sparkles, Zap } from 'lucide-react';
import { getDailyQuote } from '../data/motivationalQuotes';

export const Dashboard = () => {
  // Get consistent daily quote based on day of year
  const dailyQuote = getDailyQuote();
  
  const [dailyData, setDailyData] = useState({
    date: new Date().toISOString().split('T')[0],
    dials: 0,
    contacts_reached: 0,
    appointments_set: 0,
    presentations: 0,
    life_apps: 0,
    base_premium: 0,
    recruits_contacted: 0,
    recruits_opp_meeting: 0,
    recruits_signed: 0,
    field_training: 0,
    hunt_messages: 0,
    farm_messages: 0,
    cultivate_messages: 0,
    presentations_given: 0,
    objections_logged: 0,
    products_sold: {},
    investment_dollar_amount: 0,
    social_prospecting: {}
  });
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTodayData = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get(`/activity/daily?start_date=${today}&end_date=${today}`);
      if (response.data.length > 0) {
        setDailyData(response.data[0]);
      }
    } catch (error) {
      // Silent fail - data will show as empty
    }
  }, []);

  const fetchWeeklyData = useCallback(async () => {
    try {
      const today = new Date();
      const monday = new Date(today);
      monday.setDate(today.getDate() - today.getDay() + 1);
      const weekStart = monday.toISOString().split('T')[0];
      
      const response = await api.get(`/activity/weekly?week_start=${weekStart}`);
      setWeeklyData(response.data);
    } catch (error) {
      // Silent fail - weekly data will show as empty
    }
  }, []);

  const fetchMonthlyData = useCallback(async () => {
    try {
      const today = new Date();
      const response = await api.get(`/activity/monthly?year=${today.getFullYear()}&month=${today.getMonth() + 1}`);
      setMonthlyData(response.data);
    } catch (error) {
      // console.error('Failed to fetch monthly data:', error);
    }
  }, []);

  useEffect(() => {
    fetchWeeklyData();
    fetchMonthlyData();
    fetchTodayData();
  }, [fetchWeeklyData, fetchMonthlyData, fetchTodayData]);

  const handleSaveDaily = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/activity/daily', dailyData);
      toast.success('Daily activity saved successfully!');
      fetchWeeklyData();
      fetchMonthlyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save activity');
    } finally {
      setLoading(false);
    }
  };

  const sprintProgress = () => {
    if (!monthlyData) return 0;
    const appsProgress = Math.min((monthlyData.total_life_apps / 3) * 100, 100);
    const recruitsProgress = Math.min((monthlyData.total_recruits_signed / 3) * 100, 100);
    const bpProgress = Math.min((monthlyData.total_base_premium / 3000) * 100, 100);
    return (appsProgress + recruitsProgress + bpProgress) / 3;
  };

  const platformData = weeklyData?.social_prospects_by_platform 
    ? Object.entries(weeklyData.social_prospects_by_platform).map(([name, value]) => ({ name, value }))
    : [];

  const COLORS = ['#E31837', '#333333', '#1D4ED8', '#15803D', '#C2410C'];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Motivational Banner */}
      <div className="relative overflow-hidden bg-gradient-to-r from-[#E31837] to-[#8B0000] rounded-xl p-6 text-white shadow-lg">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-8 -mb-8"></div>
        <div className="relative z-10 flex items-center gap-4">
          <div className="bg-white/20 rounded-full p-3">
            <Zap className="h-8 w-8" />
          </div>
          <div className="flex-1">
            <p className="text-xl font-bold italic">&ldquo;{dailyQuote.text}&rdquo;</p>
            <p className="text-white/80 text-sm mt-1">— {dailyQuote.author} • Your Daily Motivation</p>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-white/20 rounded-lg px-4 py-2">
            <Sparkles className="h-5 w-5" />
            <span className="font-semibold">Day {new Date().getDate()}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#333333]">Activity Tracker Dashboard</h1>
      </div>

      {/* Daily Entry Form */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 text-[#E31837]">Daily Activity Entry</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              value={dailyData.date}
              onChange={(e) => setDailyData({ ...dailyData, date: e.target.value })}
              data-testid="daily-date-input"
            />
          </div>
          <div>
            <Label htmlFor="dials">Dials / Calls Made</Label>
            <Input
              id="dials"
              type="number"
              value={dailyData.dials}
              onChange={(e) => setDailyData({ ...dailyData, dials: parseInt(e.target.value) || 0 })}
              data-testid="dials-input"
            />
          </div>
          <div>
            <Label htmlFor="contacts">Contacts Reached</Label>
            <Input
              id="contacts"
              type="number"
              value={dailyData.contacts_reached}
              onChange={(e) => setDailyData({ ...dailyData, contacts_reached: parseInt(e.target.value) || 0 })}
              data-testid="contacts-input"
            />
          </div>
          <div>
            <Label htmlFor="appointments">Appointments Set</Label>
            <Input
              id="appointments"
              type="number"
              value={dailyData.appointments_set}
              onChange={(e) => setDailyData({ ...dailyData, appointments_set: parseInt(e.target.value) || 0 })}
              data-testid="appointments-input"
            />
          </div>
          <div>
            <Label htmlFor="presentations">Presentations / FNAs</Label>
            <Input
              id="presentations"
              type="number"
              value={dailyData.presentations}
              onChange={(e) => setDailyData({ ...dailyData, presentations: parseInt(e.target.value) || 0 })}
              data-testid="presentations-input"
            />
          </div>
          <div>
            <Label htmlFor="life_apps">Life Applications</Label>
            <Input
              id="life_apps"
              type="number"
              value={dailyData.life_apps}
              onChange={(e) => setDailyData({ ...dailyData, life_apps: parseInt(e.target.value) || 0 })}
              data-testid="life-apps-input"
            />
          </div>
          <div>
            <Label htmlFor="base_premium">Base Premium ($)</Label>
            <Input
              id="base_premium"
              type="number"
              step="0.01"
              value={dailyData.base_premium}
              onChange={(e) => setDailyData({ ...dailyData, base_premium: parseFloat(e.target.value) || 0 })}
              data-testid="base-premium-input"
            />
          </div>
          <div>
            <Label htmlFor="recruits_contacted">Recruits Contacted</Label>
            <Input
              id="recruits_contacted"
              type="number"
              value={dailyData.recruits_contacted}
              onChange={(e) => setDailyData({ ...dailyData, recruits_contacted: parseInt(e.target.value) || 0 })}
              data-testid="recruits-contacted-input"
            />
          </div>
          <div>
            <Label htmlFor="recruits_opp">Recruits at Opp Meeting</Label>
            <Input
              id="recruits_opp"
              type="number"
              value={dailyData.recruits_opp_meeting}
              onChange={(e) => setDailyData({ ...dailyData, recruits_opp_meeting: parseInt(e.target.value) || 0 })}
              data-testid="recruits-opp-input"
            />
          </div>
          <div>
            <Label htmlFor="recruits_signed">New Recruits Signed</Label>
            <Input
              id="recruits_signed"
              type="number"
              value={dailyData.recruits_signed}
              onChange={(e) => setDailyData({ ...dailyData, recruits_signed: parseInt(e.target.value) || 0 })}
              data-testid="recruits-signed-input"
            />
          </div>
          <div>
            <Label htmlFor="field_training">Field Training Sessions</Label>
            <Input
              id="field_training"
              type="number"
              value={dailyData.field_training}
              onChange={(e) => setDailyData({ ...dailyData, field_training: parseInt(e.target.value) || 0 })}
              data-testid="field-training-input"
            />
          </div>
        </div>
      </Card>

      {/* Hunt/Farm/Cultivate Messaging Section */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 text-[#E31837]">Prospecting Messages (Hunt/Farm/Cultivate)</h2>
        <p className="text-sm text-gray-600 mb-4">Track your daily outbound prospecting messages by type. Goal: 50 messages/day cycling through all phases.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <Label htmlFor="hunt_messages" className="text-red-700 font-semibold">Hunt Messages</Label>
            <p className="text-xs text-gray-500 mb-2">Direct ask to new contacts</p>
            <Input
              id="hunt_messages"
              type="number"
              value={dailyData.hunt_messages}
              onChange={(e) => setDailyData({ ...dailyData, hunt_messages: parseInt(e.target.value) || 0 })}
              data-testid="hunt-messages-input"
              className="mt-1"
            />
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <Label htmlFor="farm_messages" className="text-yellow-700 font-semibold">Farm Messages</Label>
            <p className="text-xs text-gray-500 mb-2">Indirect ask for referrals</p>
            <Input
              id="farm_messages"
              type="number"
              value={dailyData.farm_messages}
              onChange={(e) => setDailyData({ ...dailyData, farm_messages: parseInt(e.target.value) || 0 })}
              data-testid="farm-messages-input"
              className="mt-1"
            />
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <Label htmlFor="cultivate_messages" className="text-green-700 font-semibold">Cultivate Messages</Label>
            <p className="text-xs text-gray-500 mb-2">No-agenda relationship touch</p>
            <Input
              id="cultivate_messages"
              type="number"
              value={dailyData.cultivate_messages}
              onChange={(e) => setDailyData({ ...dailyData, cultivate_messages: parseInt(e.target.value) || 0 })}
              data-testid="cultivate-messages-input"
              className="mt-1"
            />
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <Label htmlFor="presentations_given" className="text-blue-700 font-semibold">Presentations Given</Label>
            <p className="text-xs text-gray-500 mb-2">Full presentations delivered</p>
            <Input
              id="presentations_given"
              type="number"
              value={dailyData.presentations_given}
              onChange={(e) => setDailyData({ ...dailyData, presentations_given: parseInt(e.target.value) || 0 })}
              data-testid="presentations-given-input"
              className="mt-1"
            />
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <Label htmlFor="objections_logged" className="text-purple-700 font-semibold">Objections Logged</Label>
            <p className="text-xs text-gray-500 mb-2">Objections encountered</p>
            <Input
              id="objections_logged"
              type="number"
              value={dailyData.objections_logged}
              onChange={(e) => setDailyData({ ...dailyData, objections_logged: parseInt(e.target.value) || 0 })}
              data-testid="objections-logged-input"
              className="mt-1"
            />
          </div>
        </div>
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Today&apos;s Total Messages:</strong> {(dailyData.hunt_messages || 0) + (dailyData.farm_messages || 0) + (dailyData.cultivate_messages || 0)} / 50 goal
          </p>
        </div>
      </Card>

      {/* Products Tracker - Table Format */}
      <ProductsTrackerTable 
        productsSold={dailyData.products_sold || {}}
        onProductsChange={(products) => setDailyData({ ...dailyData, products_sold: products })}
        investmentDollarAmount={dailyData.investment_dollar_amount || 0}
        onInvestmentDollarAmountChange={(amount) => setDailyData({ ...dailyData, investment_dollar_amount: amount })}
      />

      {/* Social Media Prospecting Tracker */}
      <SocialMediaProspectingTracker 
        socialProspecting={dailyData.social_prospecting || {}}
        onSocialProspectingChange={(data) => setDailyData({ ...dailyData, social_prospecting: data })}
      />

      {/* Unified Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveDaily}
          className="bg-[#E31837] hover:bg-[#C4122C] px-8 py-6 text-lg"
          disabled={loading}
          data-testid="save-all-activity-button"
        >
          {loading ? 'Saving...' : 'Save All Daily Activity & Products'}
        </Button>
      </div>

      {/* Weekly Summary */}
      {weeklyData && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-[#E31837]">This Week&apos;s Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-[#F8F9FA] p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-5 w-5 text-[#E31837]" />
                <span className="text-sm text-gray-600">Life Apps</span>
              </div>
              <p className="text-2xl font-bold text-[#333333]" data-testid="week-apps">{weeklyData.total_life_apps}</p>
            </div>
            <div className="bg-[#F8F9FA] p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-5 w-5 text-[#E31837]" />
                <span className="text-sm text-gray-600">Base Premium</span>
              </div>
              <p className="text-2xl font-bold text-[#333333]" data-testid="week-bp">${weeklyData.total_base_premium.toFixed(2)}</p>
            </div>
            <div className="bg-[#F8F9FA] p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-5 w-5 text-[#E31837]" />
                <span className="text-sm text-gray-600">Recruits Signed</span>
              </div>
              <p className="text-2xl font-bold text-[#333333]" data-testid="week-recruits">{weeklyData.total_recruits_signed}</p>
            </div>
            <div className="bg-[#F8F9FA] p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-[#E31837]" />
                <span className="text-sm text-gray-600">FNAs Completed</span>
              </div>
              <p className="text-2xl font-bold text-[#333333]" data-testid="week-fnas">{weeklyData.total_fnas}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Sprint to District Progress */}
      {monthlyData && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-[#E31837]">Sprint to District (30 Days)</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">Overall Progress</span>
                <span className="text-sm font-bold" data-testid="s2d-progress">{sprintProgress().toFixed(0)}%</span>
              </div>
              <Progress value={sprintProgress()} className="h-3" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <p className="text-sm text-gray-600">Life Apps: {monthlyData.total_life_apps} / 3</p>
                <Progress value={Math.min((monthlyData.total_life_apps / 3) * 100, 100)} className="mt-1" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Recruits: {monthlyData.total_recruits_signed} / 3</p>
                <Progress value={Math.min((monthlyData.total_recruits_signed / 3) * 100, 100)} className="mt-1" />
              </div>
              <div>
                <p className="text-sm text-gray-600">BP: ${monthlyData.total_base_premium.toFixed(0)} / $3,000</p>
                <Progress value={Math.min((monthlyData.total_base_premium / 3000) * 100, 100)} className="mt-1" />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Social Media Prospects Chart */}
      {platformData.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-[#E31837]">Social Media Prospects This Week</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={platformData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {platformData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
};
